import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api } from "@/lib/api";
import Lightbox from "@/components/Lightbox";
import MuseumRoom3D from "@/components/MuseumRoom3D";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    return window.innerWidth < breakpoint || hasTouch;
  });
  useEffect(() => {
    const check = () => {
      const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
      setIsMobile(window.innerWidth < breakpoint || hasTouch);
    };
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);
  return isMobile;
}

const FALLBACK_ARTWORKS = [
  {
    id: "demo-1",
    title: "Verdant Reverie",
    year: 2024,
    medium: "Oil on linen",
    image_data: "https://images.unsplash.com/photo-1557264322-b44d383a2906?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBwYWludGluZ3xlbnwwfHx8fDE3ODI2NzQ1OTh8MA&ixlib=rb-4.1.0&q=85",
    availability: "available",
  },
  {
    id: "demo-2",
    title: "Memory of Light",
    year: 2023,
    medium: "Mixed media on canvas",
    image_data: "https://images.unsplash.com/photo-1541512416146-3cf58d6b27cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBwYWludGluZ3xlbnwwfHx8fDE3ODI2NzQ1OTh8MA&ixlib=rb-4.1.0&q=85",
    availability: "sold",
  },
  {
    id: "demo-3",
    title: "Undertow",
    year: 2024,
    medium: "Acrylic on board",
    image_data: "https://images.unsplash.com/photo-1566410824233-a8011929225c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjBwYWludGluZ3xlbnwwfHx8fDE3ODI2NzQ1OTh8MA&ixlib=rb-4.1.0&q=85",
    availability: "available",
  },
  {
    id: "demo-4",
    title: "Marble Stillness",
    year: 2022,
    medium: "Photographic study",
    image_data: "https://images.unsplash.com/photo-1601887389937-0b02c26b602c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjbGFzc2ljJTIwc2N1bHB0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODI2NzQ2MDJ8MA&ixlib=rb-4.1.0&q=85",
    availability: "reserved",
  },
  {
    id: "demo-5",
    title: "Apollo Ascending",
    year: 2023,
    medium: "Bronze edition",
    image_data: "https://images.unsplash.com/photo-1729008712499-5e6b58420789?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjbGFzc2ljJTIwc2N1bHB0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODI2NzQ2MDJ8MA&ixlib=rb-4.1.0&q=85",
    availability: "available",
  },
];

// Hand-crafted asymmetric grid positions (12-col)
const POSITIONS = [
  { col: "md:col-start-1 md:col-span-5", row: "md:row-start-1", aspect: "aspect-[4/5]", parallax: -40 },
  { col: "md:col-start-7 md:col-span-5", row: "md:row-start-1 md:mt-32", aspect: "aspect-[3/4]", parallax: 30 },
  { col: "md:col-start-2 md:col-span-4", row: "md:mt-12", aspect: "aspect-square", parallax: -20 },
  { col: "md:col-start-8 md:col-span-4", row: "md:mt-24", aspect: "aspect-[4/5]", parallax: 50 },
  { col: "md:col-start-3 md:col-span-6", row: "md:mt-8", aspect: "aspect-[16/10]", parallax: -30 },
  { col: "md:col-start-1 md:col-span-5", row: "md:mt-20", aspect: "aspect-[3/4]", parallax: 25 },
  { col: "md:col-start-7 md:col-span-5", row: "md:mt-12", aspect: "aspect-square", parallax: -35 },
];

function HeroPainting({ art, testid, large, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(art)}
      className="hero-painting group text-left"
      data-testid={testid}
    >
      <div className="hero-spot" />
      <div className={`hero-frame ${large ? "hero-frame-lg" : ""}`}>
        <img
          src={art.image_data}
          alt={art.title}
          className="w-full h-full object-contain"
          loading="eager"
        />
      </div>
      <div className="hero-plaque">
        <span className="font-serif-display italic text-zinc-200 truncate">{art.title}</span>
        {art.year && <span className="plaque-label ml-2">{art.year}</span>}
      </div>
    </button>
  );
}

function ArtworkTile({ artwork, idx, onOpen }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const pos = POSITIONS[idx % POSITIONS.length];
  const y = useTransform(scrollYProgress, [0, 1], [pos.parallax, -pos.parallax]);
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      ref={ref}
      style={{ y }}
      className={`relative ${pos.col} ${pos.row}`}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      data-testid={`artwork-tile-${artwork.id}`}
    >
      <div className="spotlight-pool" />
      <button
        type="button"
        onClick={() => onOpen(artwork)}
        className="block relative group w-full text-left cursor-zoom-in"
        data-testid={`artwork-open-${artwork.id}`}
      >
        <motion.div
          className={`relative ${pos.aspect} overflow-hidden art-frame bg-[#141414]`}
          whileHover={{ scale: 1.015 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={artwork.thumb_data || artwork.image_data}
            alt={artwork.title}
            className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </motion.div>
        {/* Plaque */}
        <motion.div
          className="mt-8 max-w-md"
          initial={{ opacity: 0.7 }}
          animate={{ opacity: hovered ? 1 : 0.85 }}
        >
          <div className="flex items-baseline justify-between gap-4">
            <h3 className="font-serif-display text-2xl sm:text-3xl font-light leading-tight">
              {artwork.title}
            </h3>
            {artwork.year && (
              <span className="plaque-label whitespace-nowrap">{artwork.year}</span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-2 italic">{artwork.medium}</p>
          {artwork.availability && artwork.availability !== "available" && (
            <span className="inline-block mt-3 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-1">
              {artwork.availability}
            </span>
          )}
        </motion.div>
      </button>
    </motion.div>
  );
}

export default function Gallery() {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState({ gallery_title: "ART BY John Stam", artist_name: "John Stam" });
  const [lightboxArt, setLightboxArt] = useState(null);
  const isMobile = useIsMobile();
  const openLightbox = (art) => setLightboxArt(art);
  const closeLightbox = () => setLightboxArt(null);

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data)).catch(() => {});
    api
      .get("/artworks")
      .then((r) => setArtworks(r.data.length ? r.data : FALLBACK_ARTWORKS))
      .catch(() => setArtworks(FALLBACK_ARTWORKS))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="relative" data-testid="gallery-page">
      {/* Hero - 360 auto-rotating museum room. Same experience on desktop &
          mobile. Desktop gets an "Enter walk-mode" button that unlocks
          pointer-lock (WASD + mouse look). */}
      <section
        className={`relative overflow-hidden bg-[#0a2830] ${
          isMobile ? "h-[80vh] min-h-[520px]" : "h-[88vh] min-h-[600px]"
        }`}
      >
        {loading ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-[#0a2830]"
            data-testid="hero-loading"
          >
            <p className="plaque-label text-[#4FD1C5] animate-pulse">
              Lighting the gallery…
            </p>
          </div>
        ) : (
          <MuseumRoom3D
            artworks={artworks.length ? artworks : FALLBACK_ARTWORKS}
            onOpen={openLightbox}
            isMobile={isMobile}
          />
        )}

        {/* Title overlay (top) */}
        <div className="absolute top-0 inset-x-0 z-20 pointer-events-none">
          <div className="max-w-[1600px] mx-auto px-6 sm:px-10 w-full pt-6 sm:pt-12 fade-up">
            <p className="plaque-label mb-2 text-[#4FD1C5]" data-testid="hero-eyebrow">
              An Exhibition by {info.artist_name}
            </p>
            <h1 className="font-serif-display font-light text-xl sm:text-3xl lg:text-4xl leading-[1.1] tracking-tight max-w-2xl text-[#4FD1C5]">
              Step into <em className="italic">the hall</em>, where the lights are
              always low, and your mind will start to blow.
            </h1>
          </div>
        </div>

        {/* Contact CTA (top-right, desktop) */}
        <div className="hidden sm:block absolute top-8 right-10 z-20">
          <Link
            to="/inquire"
            className="pointer-events-auto inline-block text-xs tracking-[0.3em] uppercase border-b border-[#D4AF37] pb-1 text-[#D4AF37] hover:text-white hover:border-white transition-colors"
            data-testid="hero-inquire-cta"
          >
            Contact the artist →
          </Link>
        </div>
      </section>

      {/* The Hall */}
      <section className="relative max-w-[1600px] mx-auto px-6 sm:px-10 py-20">
        <div className="flex items-baseline justify-between mb-16">
          <h2 className="font-serif-display font-light text-3xl sm:text-5xl">The Collection</h2>
          <p className="plaque-label hidden sm:block">
            {artworks.length} {artworks.length === 1 ? "work" : "works"} on view
          </p>
        </div>

        {loading ? (
          <div className="text-center py-32 text-zinc-500" data-testid="gallery-loading">
            Lighting the gallery…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
            {artworks.map((art, idx) => (
              <ArtworkTile key={art.id} artwork={art} idx={idx} onOpen={openLightbox} />
            ))}
          </div>
        )}
      </section>

      {/* Lightbox: viewers click to enlarge an artwork */}
      <Lightbox artwork={lightboxArt} onClose={closeLightbox} />
    </div>
  );
}
